import { Box, FormLabel, HStack, Switch, Textarea } from '@chakra-ui/react';
import { Locale } from 'core/helpers/dateUtils';
import { FormButton } from 'library/atoms';
import { Modal } from 'library/molecules';
import DateTimeDropdown from 'library/molecules/DateTimeDropdown';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useManualActivityTimelineUpdate } from './hook';
import { ManualActivityEntryModalProps } from './types';

function ManualActivityEntryModal(props: ManualActivityEntryModalProps) {
  const {
    isOpen,
    onClose,
    onSave,
    onUpdate,
    incidentCreationTime,
    dateFormat,
    loadingState,
    editModalData,
    editMode,
    error,
    setError,
  } = props;

  const formattedTime = useMemo(() => {
    const localeDate = Locale.toFullDateTime(incidentCreationTime);
    return localeDate.toLocaleString();
  }, [incidentCreationTime]);
  const [note, setNote] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [dateString, setSelectedDateString] = useState<string>('');
  const [showInPostmortem, setShowInPostmortem] = useState<boolean>(false);
  const [disbaleSubmit, setDisableSubmit] = useState<boolean>(true);

  useEffect(() => {
    if (editModalData) {
      setNote(editModalData?.note);
      setSelectedDateString(Locale.toFullDateTime(editModalData?.timestamp));
      setSelectedDate(new Date(editModalData.timestamp));
      setShowInPostmortem(editModalData.showInPostmortem);
    }
  }, [editModalData]);

  const handleSave = useCallback(() => {
    editMode && editModalData
      ? onUpdate(note, dateString, editModalData?.id, showInPostmortem)
      : onSave(note, dateString, showInPostmortem);
  }, [note, dateString, editModalData, showInPostmortem]);

  const toggleShowPostmortem = () => {
    setDisableSubmit(false);
    setShowInPostmortem(!showInPostmortem);
  };

  const onDateUpdate = (date: Date, dateString: string) => {
    setDisableSubmit(false);
    setSelectedDate(date);
    setSelectedDateString(dateString);
  };

  const toggleModal = () => {
    setNote('');
    setSelectedDateString('');
    setSelectedDate(undefined);
    setShowInPostmortem(false);
    setDisableSubmit(true);
    onClose();
  };

  const getApplyButtonDisabledStatus = useMemo(() => {
    return !note || !dateString || disbaleSubmit;
  }, [note, dateString, disbaleSubmit]);

  return (
    <Modal
      key={'manual-activity-entry-modal'}
      isOpen={isOpen}
      onClose={() => {
        toggleModal();
      }}
      primaryButton={
        <FormButton
          title={editMode ? 'Update ' : 'Save'}
          disabled={getApplyButtonDisabledStatus || Boolean(error?.length)}
          onClick={handleSave}
          variant="primary"
          isLoading={loadingState}
        />
      }
      secondaryButton={
        <FormButton
          title="Cancel"
          onClick={() => {
            toggleModal();
          }}
          variant="secondary"
        />
      }
      body={
        <Box>
          <FormLabel
            as="legend"
            fontSize="14px"
            fontWeight={700}
            htmlFor="manual-activity-entry-note"
            color={'#627C98'}
          >
            Note
          </FormLabel>
          <Textarea
            id="manual-activity-entry-note-input"
            placeholder="Type details"
            value={note}
            onChange={e => {
              setDisableSubmit(false);
              setNote(e.target.value);
              setError();
            }}
            height={'15vh'}
          />
          <DateTimeDropdown
            selectedDate={selectedDate as Date}
            maxDate={new Date()}
            minDate={new Date(incidentCreationTime)}
            onUpdateRange={(date: Date, dateString: string) => {
              onDateUpdate(date, dateString);
              setError();
            }}
            dateString={dateString}
          />
          <HStack>
            <FormLabel
              as="legend"
              fontSize="14px"
              fontWeight={700}
              htmlFor="manual-activity-entry-note"
              color={'#627C98'}
              style={{ marginTop: '10px' }}
            >
              Show in Postmortem
            </FormLabel>
            <Switch
              size="sm"
              isChecked={showInPostmortem}
              disabled={false}
              onChange={e => toggleShowPostmortem()}
            />
          </HStack>
          {error && (
            <FormLabel
              as="legend"
              fontSize="14px"
              fontWeight={700}
              htmlFor="manual-activity-entry-note"
              color={'#D14C44;'}
            >
              {error}
            </FormLabel>
          )}
          <FormLabel
            as="legend"
            fontSize="14px"
            fontWeight={700}
            htmlFor="manual-activity-entry-note"
            color={'#627C98'}
          >
            Note : You can only add timeline updates from {formattedTime}, the date and time of
            incident creation
          </FormLabel>
        </Box>
      }
      title="Manually Update Activity Timeline"
    />
  );
}

export default React.memo(ManualActivityEntryModal);
